/**
 * Runtime GraphQL query builder.
 * Dynamically constructs operations from an introspected schema
 * with user-controlled depth and field selection.
 */
import {
  isAbstractType,
  isEnumType,
  isListType,
  isNonNullType,
  isObjectType,
  isScalarType,
  type GraphQLAbstractType,
  type GraphQLField,
  type GraphQLNamedType,
  type GraphQLObjectType,
  type GraphQLOutputType,
  type GraphQLSchema,
} from 'graphql';

export const CLI_OPERATION_PREFIX = 'SlCli_';

// ── Helpers ──

function unwrap(type: GraphQLOutputType): GraphQLNamedType {
  if (isNonNullType(type)) return unwrap(type.ofType);
  if (isListType(type)) return unwrap(type.ofType);
  return type as GraphQLNamedType;
}

function isLeaf(type: GraphQLNamedType): boolean {
  return isScalarType(type) || isEnumType(type);
}

function ucfirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Payload types where `id` often fails at runtime
const PAYLOAD_TYPE_PATTERN = /(?:Response|Payload|MutationPayload)$/;
// What a mutation payload carries besides its result: an id that often fails at runtime, and
// Relay's echo of an input the CLI never sends.
const PAYLOAD_PLUMBING = new Set(['id', 'clientMutationId']);

/**
 * A mutation payload is a wrapper around the result, not a level of it: at the default depth a
 * caller wants the account the mutation returns, not the wrapper's scalars. So the wrapper is
 * crossed for free, the way a connection's `nodes` is.
 */
function effectiveDepth(returnType: GraphQLNamedType, depth: number): number {
  return isObjectType(returnType) && PAYLOAD_TYPE_PATTERN.test(returnType.name) ? depth + 1 : depth;
}

// ── Selection set builder ──

export interface QueryOptions {
  /** Max nesting depth for sub-objects (default: 0) */
  depth?: number;
  /** Only include these fields (dot-notation for nested, e.g. "organization.name") */
  fields?: string[];
  /** Exclude these fields (dot-notation for nested) */
  exclude?: string[];
  /** Select `totalCount` on connections (default: false) */
  totalCount?: boolean;
}

function buildSelections(
  type: GraphQLNamedType,
  schema: GraphQLSchema,
  depth: number,
  exclude: Set<string>,
  prefix: string,
  totalCount: boolean,
  visited: Set<string>,
): string[] {
  if (!isObjectType(type)) return [];
  if (visited.has(type.name)) return [];
  visited.add(type.name);

  const fields = type.getFields();
  const lines: string[] = [];
  const isPayload = PAYLOAD_TYPE_PATTERN.test(type.name);

  // Relay Connection pattern. The connection/`nodes` wrapper is not a semantic
  // nesting level, so the node type inherits the caller's `depth` unchanged —
  // otherwise object fields on the node (e.g. Contact.organization) would never
  // be selected regardless of --depth.
  if (isConnection(type)) {
    const nodesType = unwrap(fields.nodes.type);
    const nodeSelections = buildSelections(
      nodesType,
      schema,
      depth,
      exclude,
      `${prefix}nodes.`,
      totalCount,
      new Set(visited),
    );
    return connectionSelections(type, nodeSelections, totalCount);
  }

  for (const [name, field] of Object.entries(fields)) {
    if (name.startsWith('__')) continue;
    if (isPayload && PAYLOAD_PLUMBING.has(name)) continue;

    const fullPath = `${prefix}${name}`;
    if (exclude.has(fullPath) || exclude.has(name)) continue;

    // Skip fields with required arguments
    const hasRequiredArgs = field.args.some((a) => isNonNullType(a.type) && a.defaultValue === undefined);
    if (hasRequiredArgs) continue;

    const namedType = unwrap(field.type);

    if (isLeaf(namedType)) {
      lines.push(name);
    } else if (isObjectType(namedType) && depth > 0) {
      const subSelections = buildSelections(
        namedType,
        schema,
        depth - 1,
        exclude,
        `${fullPath}.`,
        totalCount,
        new Set(visited),
      );
      if (subSelections.length > 0) {
        lines.push(`${fieldCall(name, field)} {`);
        for (const sel of subSelections) lines.push(`  ${sel}`);
        lines.push(`}`);
      }
    } else if (isAbstractType(namedType) && depth > 0) {
      lines.push(`${name} {`);
      for (const sel of abstractSelections(
        namedType,
        schema,
        depth - 1,
        exclude,
        `${fullPath}.`,
        totalCount,
        visited,
      )) {
        lines.push(`  ${sel}`);
      }
      lines.push(`}`);
    }
  }

  return lines;
}

/**
 * A union or interface is read through its members: `__typename` says which one
 * came back, and one inline fragment per possible type carries that type's fields,
 * so a payload's `authentication` renders as the hosted link or the accepted
 * credentials, whichever the subgraph answered.
 */
function abstractSelections(
  type: GraphQLAbstractType,
  schema: GraphQLSchema,
  depth: number,
  exclude: Set<string>,
  prefix: string,
  totalCount: boolean,
  visited: Set<string>,
): string[] {
  const lines = ['__typename'];
  for (const member of schema.getPossibleTypes(type)) {
    const memberSelections = buildSelections(member, schema, depth, exclude, prefix, totalCount, new Set(visited));
    if (memberSelections.length === 0) continue;
    lines.push(`... on ${member.name} {`);
    for (const sel of memberSelections) lines.push(`  ${sel}`);
    lines.push(`}`);
  }
  return lines;
}

/** A nested connection left unbounded is costed at its max page size per parent row, which blows the complexity budget. */
const NESTED_PAGE_SIZE = 10;

function fieldCall(name: string, field: GraphQLField<unknown, unknown>): string {
  const named = unwrap(field.type);
  const pageable = isObjectType(named) && isConnection(named) && field.args.some((a) => a.name === 'first');
  return pageable ? `${name}(first: ${NESTED_PAGE_SIZE})` : name;
}

function isConnection(type: GraphQLObjectType): boolean {
  const fields = type.getFields();
  return type.name.endsWith('Connection') && Boolean(fields.nodes) && Boolean(fields.pageInfo);
}

function selectableFields(type: GraphQLObjectType): GraphQLField<unknown, unknown>[] {
  return Object.values(type.getFields()).filter(
    (field) =>
      !field.name.startsWith('__') && !field.args.some((a) => isNonNullType(a.type) && a.defaultValue === undefined),
  );
}

/**
 * What the edge carries besides the node — a reveal report, a distance, a
 * filter match. Selected whenever present, one level deep: these are small
 * value objects that describe the row, and losing them for the sake of a
 * shorter query is losing the answer.
 */
function edgeExtras(connection: GraphQLObjectType): { edge: GraphQLObjectType; lines: string[] } | null {
  const edgesField = connection.getFields().edges;
  if (!edgesField) return null;
  const edge = unwrap(edgesField.type);
  if (!isObjectType(edge)) return null;

  const lines: string[] = [];
  for (const field of selectableFields(edge)) {
    if (field.name === 'node' || field.name === 'cursor' || field.name === 'id') continue;
    const named = unwrap(field.type);
    if (isLeaf(named)) {
      lines.push(field.name);
    } else if (isObjectType(named)) {
      const leaves = selectableFields(named)
        .filter((sub) => isLeaf(unwrap(sub.type)))
        .map((sub) => sub.name);
      if (leaves.length > 0) lines.push(`${field.name} { ${leaves.join(' ')} }`);
    }
  }

  return lines.length === 0 ? null : { edge, lines };
}

/**
 * `nodes { … }` when the edge is a plain wrapper; `edges { node { … } … }` when
 * it carries more, so what the server says about each row comes along.
 */
function connectionSelections(connection: GraphQLObjectType, nodeSelections: string[], totalCount: boolean): string[] {
  if (nodeSelections.length === 0) return [];
  const lines: string[] = [];
  const extras = edgeExtras(connection);

  if (extras) {
    lines.push('edges {');
    lines.push('  node {');
    for (const sel of nodeSelections) lines.push(`    ${sel}`);
    lines.push('  }');
    for (const extra of extras.lines) lines.push(`  ${extra}`);
    lines.push('}');
  } else {
    lines.push('nodes {');
    for (const sel of nodeSelections) lines.push(`  ${sel}`);
    lines.push('}');
  }
  lines.push('pageInfo {');
  lines.push('  hasNextPage');
  lines.push('  endCursor');
  lines.push('}');
  if (totalCount && connection.getFields().totalCount) lines.push('totalCount');
  for (const extra of connectionExtras(connection)) lines.push(extra);
  return lines;
}

const CONNECTION_PLUMBING = new Set(['edges', 'nodes', 'pageInfo', 'totalCount', 'approximateTotalCount']);

/**
 * What a connection says about the page as a whole — the bill of a reveal, for
 * one — selected two levels deep so a summary with a per-item split comes along.
 */
function connectionExtras(connection: GraphQLObjectType): string[] {
  return selectableFields(connection)
    .filter((field) => !CONNECTION_PLUMBING.has(field.name))
    .flatMap((field) => {
      const named = unwrap(field.type);
      if (isLeaf(named)) return [field.name];
      if (!isObjectType(named)) return [];
      const inner = selectableFields(named).flatMap((sub) => {
        const subType = unwrap(sub.type);
        if (isLeaf(subType)) return [sub.name];
        if (!isObjectType(subType)) return [];
        const leaves = selectableFields(subType).filter((leaf) => isLeaf(unwrap(leaf.type)));
        return leaves.length === 0 ? [] : [`${sub.name} { ${leaves.map((leaf) => leaf.name).join(' ')} }`];
      });
      return inner.length === 0 ? [] : [`${field.name} { ${inner.join(' ')} }`];
    });
}

/**
 * A misspelt field used to be dropped silently, so `--fields email` printed
 * every other column and left the caller wondering where the email went.
 */
function fieldNamesOf(type: GraphQLNamedType): string[] {
  return isObjectType(type) ? Object.keys(type.getFields()) : [];
}

export class UnknownFieldError extends Error {
  readonly field: string;
  readonly suggestions: string[];

  constructor(field: string, type: GraphQLNamedType, candidates: string[] = fieldNamesOf(type)) {
    const names = candidates.filter((name) => !name.startsWith('__'));
    const needle = field.toLowerCase();
    const suggestions = names.filter((name) => {
      const candidate = name.toLowerCase();
      return candidate.includes(needle) || needle.includes(candidate);
    });
    super(`${type.name} has no field "${field}".`);
    this.name = 'UnknownFieldError';
    this.field = field;
    this.suggestions = suggestions.length > 0 ? suggestions : names;
  }
}

// ── Public API ──

export interface BuiltQuery {
  query: string;
  variables: Record<string, unknown>;
  /** The root field name (e.g. "agency", "createChat") */
  fieldName: string;
}

/**
 * Build a GraphQL query/mutation string for a root operation field.
 *
 * @param schema - The introspected GraphQL schema
 * @param kind - "query" or "mutation"
 * @param fieldName - The root field name (e.g. "agency", "createChat")
 * @param variables - Variable values to send
 * @param options - Depth, field inclusion/exclusion
 */
export type OperationKind = 'query' | 'mutation' | 'subscription';

export function rootTypeOf(schema: GraphQLSchema, kind: OperationKind): GraphQLObjectType | null | undefined {
  if (kind === 'query') return schema.getQueryType();
  if (kind === 'mutation') return schema.getMutationType();
  return schema.getSubscriptionType();
}

export function buildQuery(
  schema: GraphQLSchema,
  kind: OperationKind,
  fieldName: string,
  variables: Record<string, unknown>,
  options: QueryOptions = {},
): BuiltQuery {
  const rootType = rootTypeOf(schema, kind);
  if (!rootType) throw new Error(`No ${kind} type in schema`);

  const field = rootType.getFields()[fieldName];
  if (!field) throw new Error(`Unknown ${kind} field: ${fieldName}`);

  const returnType = unwrap(field.type);
  const depth = effectiveDepth(returnType, options.depth ?? 0);
  const exclude = new Set(options.exclude ?? []);
  const wantsTotalCount = options.totalCount === true;

  // Build variable definitions from the field's arguments
  const allArgs = field.args;

  const varDefsInner = allArgs.map((a) => `$${a.name}: ${a.type.toString()}`).join(', ');
  const fieldArgsInner = allArgs.map((a) => `${a.name}: $${a.name}`).join(', ');
  const varDefs = allArgs.length > 0 ? `(${varDefsInner})` : '';
  const fieldArgs = allArgs.length > 0 ? `(${fieldArgsInner})` : '';

  // Build selection set
  let selectionBody: string;
  if (isLeaf(returnType)) {
    selectionBody = `    ${fieldName}${fieldArgs}`;
  } else if (isAbstractType(returnType)) {
    const indent = '      ';
    const body = abstractSelections(returnType, schema, depth, exclude, '', wantsTotalCount, new Set())
      .map((s) => `${indent}${s}`)
      .join('\n');
    selectionBody = `    ${fieldName}${fieldArgs} {\n${body}\n    }`;
  } else {
    // Filter fields if --fields is provided
    let selections: string[];
    if (options.fields && options.fields.length > 0) {
      selections = buildFieldsFromList(returnType, schema, options.fields, wantsTotalCount);
    } else {
      selections = buildSelections(returnType, schema, depth, exclude, '', wantsTotalCount, new Set());
    }

    if (selections.length === 0) {
      // Fallback: at minimum select __typename
      selectionBody = `    ${fieldName}${fieldArgs} {\n      __typename\n    }`;
    } else {
      const indent = '      ';
      const body = selections.map((s) => `${indent}${s}`).join('\n');
      selectionBody = `    ${fieldName}${fieldArgs} {\n${body}\n    }`;
    }
  }

  const opName = `${CLI_OPERATION_PREFIX}${ucfirst(fieldName)}`;
  const query = `${kind} ${opName}${varDefs} {\n${selectionBody}\n  }`;

  return { query, variables, fieldName };
}

/**
 * Build selections from an explicit field list (e.g. --fields id,name,slug).
 */
function buildFieldsFromList(
  type: GraphQLNamedType,
  schema: GraphQLSchema,
  fieldPaths: string[],
  totalCount: boolean,
): string[] {
  if (!isObjectType(type)) return [];
  const fields = type.getFields();

  // Relay Connection pattern — unwrap to the node type. The id always comes
  // along: it is what a row is titled by and what the next command is pasted.
  if (isConnection(type)) {
    const nodesType = unwrap(fields.nodes.type);
    const withId = isObjectType(nodesType) && nodesType.getFields().id && !fieldPaths.includes('id');
    const nodeSelections = buildFieldsFromList(
      nodesType,
      schema,
      withId ? ['id', ...fieldPaths] : fieldPaths,
      totalCount,
    );
    return connectionSelections(type, nodeSelections, totalCount);
  }

  const lines: string[] = [];

  // Group by top-level field
  const grouped = new Map<string, string[]>();
  for (const fp of fieldPaths) {
    const [top, ...rest] = fp.split('.');
    if (!grouped.has(top)) grouped.set(top, []);
    if (rest.length > 0) grouped.get(top)!.push(rest.join('.'));
  }

  for (const [name, subPaths] of grouped) {
    const field = fields[name];
    if (!field) throw new UnknownFieldError(name, type);

    const namedType = unwrap(field.type);
    if (isLeaf(namedType)) {
      lines.push(name);
    } else if (isObjectType(namedType) && subPaths.length > 0) {
      const subSelections = buildFieldsFromList(namedType, schema, subPaths, totalCount);
      if (subSelections.length > 0) {
        lines.push(`${fieldCall(name, field)} {`);
        for (const sel of subSelections) lines.push(`  ${sel}`);
        lines.push(`}`);
      }
    } else if (isAbstractType(namedType)) {
      lines.push(`${name} {`);
      for (const sel of abstractFieldsFromList(namedType, schema, subPaths, totalCount)) lines.push(`  ${sel}`);
      lines.push(`}`);
    }
  }

  return lines;
}

/**
 * `--fields authentication.url` names a field of one member; each member takes
 * the paths it knows, and a path no member knows is refused against the union
 * itself. Without sub-paths, every member's leaf fields are selected.
 */
function abstractFieldsFromList(
  type: GraphQLAbstractType,
  schema: GraphQLSchema,
  subPaths: string[],
  totalCount: boolean,
): string[] {
  const members = schema.getPossibleTypes(type);
  if (subPaths.length === 0) {
    return abstractSelections(type, schema, 0, new Set(), '', totalCount, new Set());
  }

  const lines = ['__typename'];
  const claimed = new Set<string>();
  for (const member of members) {
    const known = subPaths.filter((fp) => Boolean(member.getFields()[fp.split('.')[0]]));
    if (known.length === 0) continue;
    for (const fp of known) claimed.add(fp);
    lines.push(`... on ${member.name} {`);
    for (const sel of buildFieldsFromList(member, schema, known, totalCount)) lines.push(`  ${sel}`);
    lines.push(`}`);
  }
  const unknown = subPaths.find((fp) => !claimed.has(fp));
  if (unknown !== undefined) {
    throw new UnknownFieldError(unknown.split('.')[0], type, members.flatMap(fieldNamesOf));
  }

  return lines;
}
